import React, { useEffect, useRef, useCallback } from 'react';
import { GTMContext, InitState } from '@/contexts/gtm';
import { EventName, EVENT_MAPPING } from '@/types/events';
import { logger } from '@/utils/logger';
import { unifiedEventDispatcher } from '@/tracking/gtm/services/unified-event-dispatcher';
import { normalizeEvent } from '@/utils/event-normalizer';

type EventListener = (evt: Event) => void;
type EventListenerObject = {
  handleEvent(evt: Event): void;
};

type EventListenerOrEventListenerObject = EventListener | EventListenerObject;

// Custom event types
interface VideoEventDetail {
  eventParams: {
    video_status: 'started' | 'completed' | 'progress';
    cf_stream_id: string;
    cf_player_version: string;
    cf_quality: string;
    cf_stream_duration: number;
    cf_stream_current_time: number;
    cf_stream_percent: number;
    funnel_path: string;
    engagement_time_msec: number;
    milestone?: number;
  };
}

type VideoCustomEvent = CustomEvent<VideoEventDetail>;

const generateUserId = () => {
  const existingUserId = document.cookie.match('(?:^|;)\\s*_uid=([^;]*)');
  if (existingUserId) return existingUserId[1];

  const newUserId = 'uid_' + Date.now() + '_' + Math.random().toString(36).substring(2);
  const domain = window.location.hostname.split('.').slice(-2).join('.');
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 2);

  document.cookie = '_uid=' + newUserId +
    ';domain=' + domain +
    ';path=/' +
    ';expires=' + expires.toUTCString() +
    ';SameSite=Lax' +
    ';Secure';
  return newUserId;
};

const generateClientId = () => {
  const existingClientId = document.cookie.match('(?:^|;)\\s*_ga_client_id=([^;]*)');
  if (existingClientId) return existingClientId[1];

  const newClientId = 'ga_' + Date.now() + '_' + Math.random().toString(36).substring(2);
  document.cookie = '_ga_client_id=' + newClientId +
    ';path=/' +
    ';max-age=' + (2 * 365 * 24 * 60 * 60) +
    ';SameSite=Lax' +
    ';Secure';
  return newClientId;
};

const GTMProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const initState = useRef<InitState>({
    supabase: false,
    ga4: false,
    fb: false,
    cf: false,
    initialized: false,
    systemReady: false
  });

  const systemEvents = useRef<Set<string>>(new Set());

  const eventQueue = useRef<Array<{
    eventName: EventName;
    data?: any;
  }>>([]);

  const isInitialized = useRef(false);
  const isInitializing = useRef(false);
  const rafTimeout = useRef<number | null>(null);

  const processEventQueue = useCallback(() => {
    if (!isInitialized.current || !eventQueue.current.length) return;

    while (eventQueue.current.length > 0) {
      const event = eventQueue.current.shift();
      if (event) {
        unifiedEventDispatcher.pushUnifiedEvent(event.eventName, event.data, {
          ...initState.current,
          gtmReady: true
        });
      }
    }
  }, []);

  const pushEvent = useCallback((eventName: EventName, data?: any) => {
    if (!isInitialized.current) {
      eventQueue.current.push({ eventName, data });
      logger.info(`[GTM] Event queued: ${eventName}`);
      return;
    }

    unifiedEventDispatcher.pushUnifiedEvent(eventName, data, {
      ...initState.current,
      gtmReady: true
    });
  }, []);

  const handleVideoStart = useCallback((event: VideoCustomEvent): void => {
    pushEvent('VIDEO_START', {
      detail: {
        ...event.detail,
        eventParams: {
          ...event.detail.eventParams,
          event_timestamp: Date.now()
        }
      }
    });
  }, [pushEvent]);

  const handleVideoProgress = useCallback((event: VideoCustomEvent): void => {
    pushEvent('VIDEO_PROGRESS', {
      detail: {
        ...event.detail,
        eventParams: {
          ...event.detail.eventParams,
          event_timestamp: Date.now()
        }
      }
    });
  }, [pushEvent]);

  const handleVideoComplete = useCallback((event: VideoCustomEvent): void => {
    pushEvent('VIDEO_COMPLETE', {
      detail: {
        ...event.detail,
        eventParams: {
          ...event.detail.eventParams,
          event_timestamp: Date.now()
        }
      }
    });
  }, [pushEvent]);

  useEffect(() => {
    if (isInitialized.current || isInitializing.current || initState.current.systemReady) return;
    isInitializing.current = true;

    const environment = import.meta.env.VITE_ENVIRONMENT || 'development';
    const ga4Id = import.meta.env.VITE_GA_MEASUREMENT_ID;
    const gtmContainerId = import.meta.env.VITE_GTM_ID;
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

    window.dataLayer = window.dataLayer || [];

    // Track system event sequence
    const trackSystemEvent = (eventName: string) => {
      if (!systemEvents.current.has(eventName)) {
        systemEvents.current.add(eventName);
        logger.info(`[GTM] System event processed: ${eventName}`);
      }
    };

    // 1. Initialize user ID (0.07)
    const userId = generateUserId();
    window.dataLayer.push({
      'event': '0.07_SET_USER_ID',
      'user_id': userId,
      'detail': {
        'eventParams': {
          'timestamp': Date.now(),
          'event_sequence': '0.07'
        }
      }
    });
    trackSystemEvent('0.07_SET_USER_ID');

    // 2. Set client ID (0.08)
    const clientId = generateClientId();
    window.dataLayer.push({
      'event': '0.08_SET_CLIENT_ID',
      'client_id': clientId,
      'detail': {
        'eventParams': {
          'timestamp': Date.now(),
          'event_sequence': '0.08'
        }
      }
    });
    trackSystemEvent('0.08_SET_CLIENT_ID');

    // 3. Push FB data hash (0.09)
    window.dataLayer.push({
      'event': '0.09_FB_DATA_HASH',
      'detail': {
        'eventParams': {
          'timestamp': Date.now(),
          'event_sequence': '0.09',
          'user_id': userId,
          'client_id': clientId
        }
      }
    });
    trackSystemEvent('0.09_FB_DATA_HASH');

    // 4. Initialize GTM config
    if (!window.EVENT_CONFIG) {
      window.EVENT_CONFIG = {
        GTM_CONFIG: {
          environment,
          debug: false,
          ga4Id,
          gtmContainerId,
          supabaseUrl
        },
        MAPPING: EVENT_MAPPING,
        normalize: normalizeEvent
      };
    }

    // 6. Initialize base tag (0.01)
    window.dataLayer.push({
      'event': '0.01_UNIFIED_BASE_TAG',
      'detail': {
        'eventParams': {
          'pageViewCount': 1,
          'timestamp': Date.now(),
          'page_location': window.location.href,
          'page_title': document.title,
          'environment': environment,
          'event_sequence': '0.01',
          'user_id': userId,
          'client_id': clientId
        }
      }
    });
    trackSystemEvent('0.01_UNIFIED_BASE_TAG');

    // 7. Push event distributor (0.02)
    window.dataLayer.push({
      'event': '0.02_EVENT_DISTRIBUTOR',
      'detail': {
        'eventParams': {
          'timestamp': Date.now(),
          'event_sequence': '0.02',
          'user_id': userId,
          'client_id': clientId
        }
      }
    });
    trackSystemEvent('0.02_EVENT_DISTRIBUTOR');

    // 8. Push CAPI handler (0.03)
    window.dataLayer.push({
      'event': '0.03_CAPI_HANDLER_TAG',
      'detail': {
        'eventParams': {
          'timestamp': Date.now(),
          'event_sequence': '0.03',
          'user_id': userId,
          'client_id': clientId
        }
      }
    });
    trackSystemEvent('0.03_CAPI_HANDLER_TAG');

    // 9. Mark GA4 as initialized
    initState.current.ga4 = true;

    // Initialize services
    const handleSupabaseInit = () => {
      initState.current.supabase = true;
      logger.info('[GTM] Supabase initialized');
    };

    const handleCloudflareInit = () => {
      initState.current.cf = true;
      logger.info('[GTM] Cloudflare initialized');
    };

    window.addEventListener('supabase-initialized', handleSupabaseInit, { once: true });
    window.addEventListener('cf_ready', handleCloudflareInit, { once: true });

    // Check FB initialization with RAF
    const waitForFb = new Promise<void>((resolve) => {
      let startTime = performance.now();
      const MAX_WAIT_TIME = 5000;

      const checkFbWithRAF = (timestamp: number) => {
        if (typeof window.fbq === 'function') {
          initState.current.fb = true;
          resolve();
          return;
        }

        if (timestamp - startTime >= MAX_WAIT_TIME) {
          resolve(); // Resolve anyway after timeout
          return;
        }

        rafTimeout.current = requestAnimationFrame(checkFbWithRAF);
      };

      requestAnimationFrame(checkFbWithRAF);
    });

    waitForFb.then(() => {
      // Mark as initialized and process queue
      isInitialized.current = true;
      isInitializing.current = false;
      initState.current.systemReady = true;
      logger.info('[GTM] Initialization complete');

      // Dispatch system ready event with proper sequence
      if (!systemEvents.current.has('system_ready')) {
        window.dataLayer.push({
          'event': 'system_ready',
          'gtm': {
            uniqueEventId: Date.now(),
            start: performance.now()
          },
          'detail': {
            'eventParams': {
              'event_sequence': '0.05',
              'ga4_ready': initState.current.ga4,
              'fb_ready': initState.current.fb,
              'supabase_ready': initState.current.supabase,
              'environment': environment,
              'timestamp': Date.now()
            }
          }
        });
        trackSystemEvent('system_ready');
      }

      // Process any queued events
      processEventQueue();
    }).catch(error => {
      logger.error('[GTM] Initialization error:', error);
      isInitialized.current = true; // Mark as initialized anyway to prevent hanging
      isInitializing.current = false;
      processEventQueue(); // Process queued events even if there was an error
    });

    return () => {
      window.removeEventListener('supabase-initialized', () => { });
      window.removeEventListener('cf_ready', () => { });
      if (rafTimeout.current !== null) {
        cancelAnimationFrame(rafTimeout.current);
      }
    };
  }, [processEventQueue]);

  // Listen for Cloudflare Stream events
  useEffect(() => {
    window.addEventListener('GA4_VIDEO_START', handleVideoStart as unknown as EventListenerOrEventListenerObject);
    window.addEventListener('GA4_VIDEO_PROGRESS', handleVideoProgress as unknown as EventListenerOrEventListenerObject);
    window.addEventListener('GA4_VIDEO_COMPLETE', handleVideoComplete as unknown as EventListenerOrEventListenerObject);

    return () => {
      window.removeEventListener('GA4_VIDEO_START', handleVideoStart as unknown as EventListenerOrEventListenerObject);
      window.removeEventListener('GA4_VIDEO_PROGRESS', handleVideoProgress as unknown as EventListenerOrEventListenerObject);
      window.removeEventListener('GA4_VIDEO_COMPLETE', handleVideoComplete as unknown as EventListenerOrEventListenerObject);
    };
  }, [handleVideoStart, handleVideoProgress, handleVideoComplete]);

  return (
    <GTMContext.Provider value={{ pushEvent, initState: initState.current }}>
      {children}
    </GTMContext.Provider>
  );
};

export default GTMProvider;
